// /onboarding/join — the member path (board 25 v2 erp/onboarding/join--code,
// join--invalid, join--waiting, join--accepted; copy verbatim). Three phases:
//  1. no session → the invite code (the pasted WhatsApp link or the bare
//     48-hex token; POST /v1/public/invites/lookup says «who invited me» and
//     grants nothing) → the phone;
//  2. session + memberships → «أصبحتَ ضمن فريق …» → «اليوم» in that business;
//  3. session, no memberships → «بانتظار دعوة»: the number to hand the owner,
//     a claim every minute (invites are matched BY PHONE on the server,
//     ADR-0016 / ruling S8-L10), no coral — nothing to decide yet.

import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Copy, KeyRound } from 'lucide-react';
import { Button, PrimaryAction } from '@/components/Button';
import { isApiError } from '@/lib/api';
import { sessionStore } from '@/lib/auth';
import { formatIraqiPhone } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import { DONE, JOIN, PHONE, SHELL } from '../copy';
import { failureOf, claimInvites, inviteTokenFrom, lookupInvite, type ClaimedMembership, type PublicInvite } from '../data/api';
import { ONB } from '../data/flow';
import { profileOf } from '../preview';
import { BigInput, EnterBusiness, Msg, ROLE_LABEL } from '../components/widgets';
import { Wizard, type WizardError } from '../components/Wizard';
import { onboardingStore, useOnboarding } from '../state';
import type { PathOptions } from '../steps';

/** «نتحقّق من الدعوات كل دقيقة». */
export const POLL_MS = 60_000;

/** The ghost «رجوع» of the in-place code field on the waiting screen (brief §Join). */

const CODE_PATH: Partial<PathOptions> = { intent: 'member', withCode: true };
const WAITING_PATH: Partial<PathOptions> = { intent: 'member', withCode: false };
const ACCEPTED_PATH: Partial<PathOptions> = { intent: 'member', withCode: true };

type SysError = { kind: 'network' | 'server'; ref: string | null; detail?: string | null };

/** 400 / 404 / 410 (and any other 4xx) = the code is wrong or spent; no response or 5xx = a system error. */
function isCodeRefusal(e: unknown): boolean {
  return isApiError(e) && e.status >= 400 && e.status < 500;
}

function sysErrorOf(e: unknown): SysError {
  return { ...failureOf(e) };
}

export function JoinScreen() {
  const { intent, memberships } = useOnboarding();
  const authed = sessionStore.isAuthenticated();
  if (authed && intent === 'owner') return <Navigate to={ONB.name} replace />;
  if (!authed) return <JoinCode />;
  return <JoinMember memberships={memberships} />;
}

// ------------------------------------------------------- 1. the code --

function JoinCode() {
  const navigate = useNavigate();
  const layout = useLayout();
  const [params] = useSearchParams();
  const [code, setCode] = useState(() => params.get('code') ?? '');
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sysError, setSysError] = useState<SysError | null>(null);
  const auto = useRef(false);

  const validate = async (input: string) => {
    if (busy) return;
    setSysError(null);
    const token = inviteTokenFrom(input);
    if (!token) {
      setInvalid(true);
      return;
    }
    setBusy(true);
    try {
      const res: PublicInvite = await lookupInvite(token);
      onboardingStore.set({ intent: 'member', invite: { token, ...res } });
      navigate(ONB.phone);
    } catch (e) {
      if (isCodeRefusal(e)) setInvalid(true);
      else setSysError(sysErrorOf(e));
    } finally {
      setBusy(false);
    }
  };

  // The WhatsApp link opens this screen with the code filled: check it once.
  useEffect(() => {
    if (auto.current) return;
    auto.current = true;
    const fromUrl = params.get('code');
    if (fromUrl) void validate(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noCode = () => {
    onboardingStore.set({ intent: 'member', invite: null });
    navigate(ONB.phone);
  };

  const error: WizardError | null = sysError ? { kind: sysError.kind, ref: sysError.ref, detail: sysError.detail, onRetry: () => void validate(code) } : null;

  return (
    <Wizard
      screen="join"
      testId="onb-join"
      state={busy ? 'busy' : invalid ? 'invalid' : 'code'}
      path={CODE_PATH}
      question={JOIN.title}
      sub={JOIN.sub}
      onSubmit={() => void validate(code)}
      error={error}
      enterHint
      peek={PHONE.title}
      panel={{ phase: 'identity', sub: 'join' }}
      primary={
        <PrimaryAction type="submit" size="lg" icon={ArrowLeft} loading={busy} block={layout === 'phone'} data-testid="onb-continue">
          {busy ? JOIN.busy : SHELL.continue}
        </PrimaryAction>
      }
      tertiary={
        <button type="button" className="onb-tert" data-testid="onb-no-code" onClick={noCode}>
          {JOIN.noCode}
        </button>
      }
    >
      <BigInput
        ltr
        autoFocus
        data-testid="onb-invite-code"
        aria-label={JOIN.title}
        placeholder={JOIN.placeholder}
        autoComplete="off"
        spellCheck={false}
        readOnly={busy}
        value={code}
        onChange={(v) => {
          setCode(v);
          setInvalid(false);
        }}
        error={invalid ? JOIN.invalid : undefined}
      />
    </Wizard>
  );
}

// ------------------------------------------- 2 + 3. accepted / waiting --

function JoinMember({ memberships }: { memberships: ClaimedMembership[] | null }) {
  const navigate = useNavigate();
  const layout = useLayout();
  const mobile = layout === 'phone';
  const [mode, setMode] = useState<'waiting' | 'code'>('waiting');
  const [code, setCode] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [otherPhone, setOtherPhone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sysError, setSysError] = useState<SysError | null>(null);
  const [copied, setCopied] = useState(false);
  const [enter, setEnter] = useState<string | null>(null);

  // A reload on this screen: the store lost the claim → ask once.
  const claim = useQuery({
    queryKey: ['onboarding', 'claim'],
    queryFn: claimInvites,
    enabled: memberships === null,
    retry: 0,
    staleTime: Infinity,
  });
  useEffect(() => {
    if (memberships === null && claim.data) onboardingStore.set({ memberships: claim.data });
  }, [memberships, claim.data]);

  const list = memberships ?? claim.data ?? null;
  const pending = memberships === null && claim.isPending;
  const found = (list?.length ?? 0) > 0;

  // The waiting screen keeps asking (board: «نتحقّق من الدعوات كل دقيقة»).
  const poll = useQuery({
    queryKey: ['onboarding', 'claim', 'poll'],
    queryFn: claimInvites,
    enabled: !pending && !found,
    refetchInterval: POLL_MS,
    retry: 0,
  });
  useEffect(() => {
    if (poll.data && poll.data.length > 0) onboardingStore.set({ memberships: poll.data });
  }, [poll.data]);

  if (enter) return <EnterBusiness id={enter} />;

  if (found && list) {
    const m = list[0];
    const role = ROLE_LABEL[m.role] ?? m.role;
    const profile = profileOf(m.category_id);
    return (
      <Wizard
        screen="accepted"
        testId="onb-join"
        state="accepted"
        path={ACCEPTED_PATH}
        question={JOIN.acceptedTitle(m.name_ar)}
        sub={JOIN.acceptedSub(profile)}
        onSubmit={() => setEnter(m.business_id)}
        panel={{
          phase: 'preview',
          mark: 'success',
          done: true,
          profile,
          preview: {
            name: m.name_ar,
            city: m.city || undefined,
            line: JOIN.acceptedLine(role),
            lineIcon: 'users',
            cta: profile === 'clinic' ? DONE.ctaClinic : DONE.cta,
            slug: null,
          },
        }}
        primary={
          <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} className="onb-delayed" data-testid="onb-open-today">
            {JOIN.openToday}
          </PrimaryAction>
        }
      >
        <div className="onb-pills">
          <span className="onb-pill" data-tone="info">
            {role}
          </span>
        </div>
      </Wizard>
    );
  }

  const phone = sessionStore.getUser()?.phone ?? '';

  const copyNumber = async () => {
    try {
      await navigator.clipboard?.writeText(formatIraqiPhone(phone).replace(/\s/g, ''));
      setCopied(true);
    } catch {
      /* clipboard refused — the number is on screen */
    }
  };

  const ownBusiness = () => {
    onboardingStore.set({ intent: 'owner', invite: null });
    navigate(ONB.name);
  };

  const ownBusinessLink = (
    <button type="button" className="onb-tert" data-testid="onb-own-business" onClick={ownBusiness}>
      {JOIN.ownBusiness}
    </button>
  );

  if (mode === 'code') {
    const join = async () => {
      if (busy) return;
      setSysError(null);
      setOtherPhone(null);
      const token = inviteTokenFrom(code);
      if (!token) {
        setInvalid(true);
        return;
      }
      setBusy(true);
      try {
        const invite = await lookupInvite(token);
        const ms = await claimInvites();
        if (ms.length > 0) onboardingStore.set({ memberships: ms });
        else setOtherPhone(JOIN.otherPhone(invite.business_name, ROLE_LABEL[invite.role] ?? invite.role));
      } catch (e) {
        if (isCodeRefusal(e)) setInvalid(true);
        else setSysError(sysErrorOf(e));
      } finally {
        setBusy(false);
      }
    };
    const error: WizardError | null = sysError ? { kind: sysError.kind, ref: sysError.ref, detail: sysError.detail, onRetry: () => void join() } : null;
    return (
      <Wizard
        screen="waiting"
        testId="onb-join"
        state={busy ? 'busy' : invalid ? 'invalid' : otherPhone ? 'other-phone' : 'code'}
        path={WAITING_PATH}
        question={JOIN.title}
        sub={JOIN.sub}
        onSubmit={() => void join()}
        error={error}
        enterHint
        panel={{ phase: 'identity', sub: 'join' }}
        primary={
          <PrimaryAction type="submit" size="lg" icon={ArrowLeft} loading={busy} block={mobile} data-testid="onb-continue">
            {busy ? JOIN.busy : SHELL.continue}
          </PrimaryAction>
        }
        secondary={
          <Button
            variant="ghost"
            size="lg"
            block={mobile}
            data-testid="onb-join-back"
            onClick={() => {
              setMode('waiting');
              setInvalid(false);
              setOtherPhone(null);
              setSysError(null);
            }}
          >
            {JOIN.back}
          </Button>
        }
        tertiary={ownBusinessLink}
      >
        <BigInput
          ltr
          autoFocus
          data-testid="onb-invite-code"
          aria-label={JOIN.title}
          placeholder={JOIN.placeholder}
          autoComplete="off"
          spellCheck={false}
          readOnly={busy}
          value={code}
          onChange={(v) => {
            setCode(v);
            setInvalid(false);
            setOtherPhone(null);
          }}
          error={invalid ? JOIN.invalid : undefined}
        />
        {otherPhone && (
          <Msg tone="info" testId="onb-other-phone">
            {otherPhone}
          </Msg>
        )}
      </Wizard>
    );
  }

  return (
    <Wizard
      screen="waiting"
      testId="onb-join"
      state={pending ? 'loading' : 'waiting'}
      path={WAITING_PATH}
      question={JOIN.waitingTitle}
      sub={JOIN.waitingSub}
      panel={{ phase: 'identity', sub: 'join' }}
      secondary={
        <Button variant="line" size="lg" icon={KeyRound} block={mobile} data-testid="onb-have-code" onClick={() => setMode('code')}>
          {JOIN.haveCode}
        </Button>
      }
      tertiary={ownBusinessLink}
    >
      <div className="onb-numcard">
        <div className="onb-numcard-text">
          <div className="onb-numcard-label">{JOIN.yourNumber}</div>
          <bdi dir="ltr" className="ltr onb-numcard-value" data-testid="onb-my-number">
            {formatIraqiPhone(phone)}
          </bdi>
        </div>
        <Button variant="line" size="sm" icon={Copy} onClick={() => void copyNumber()} data-testid="onb-copy-number">
          {JOIN.copy}
        </Button>
      </div>
      {copied && (
        <span className="e-field-hint" role="status">
          {JOIN.copied}
        </span>
      )}
      <div className="onb-poll" data-testid="onb-poll">
        <span className="onb-poll-dot" aria-hidden="true" />
        {JOIN.polling}
      </div>
    </Wizard>
  );
}
